import * as React from 'react';
import { Button } from '@/components/ui/Button';
import stripeLogo from '@/assets/images/svg/stripe_logo.svg';
import stripeIcon from '@/assets/images/svg/stripe.svg';
import clsx from 'clsx';
import { connectedAccountsService } from '@/services/connectedAccountsService';
import { useAuth } from '@/hooks/useAuth';

export const StripeCard: React.FC = () => {
  const { user, refreshProfile } = useAuth();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Force inactive state for development
  const IS_COMING_SOON = false;

  // Sync Stripe status on mount when account exists but onboarding appears incomplete.
  // The /status endpoint fetches live data from Stripe API and syncs it to the DB,
  // so this works even without webhooks (e.g. local dev without stripe listen).
  React.useEffect(() => {
    if (!user?.stripe_account_id || user.stripe_account_status === 'active') return;

    let cancelled = false;
    const syncStatus = async () => {
      try {
        const status = await connectedAccountsService.getStatus();
        if (cancelled) return;
        // If Stripe says onboarding is done but our cached user disagrees, refresh
        if (status.details_submitted && !user.stripe_details_submitted) {
          await refreshProfile();
        }
      } catch {
        // Silent — non-critical, user can manually refresh
      }
    };
    syncStatus();
    return () => {
      cancelled = true;
    };
  }, [
    user?.stripe_account_id,
    user?.stripe_account_status,
    user?.stripe_details_submitted,
    refreshProfile,
  ]);

  // --- Handlers ---

  // 1. Create new account
  const handleConnect = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await connectedAccountsService.create();
      window.location.href = response.onboarding_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect Stripe account');
      setIsLoading(false);
    }
  };

  // 2. Continue Setup / Action Required (Refresh Link)
  const handleRefreshLink = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await connectedAccountsService.refreshLink();
      window.location.href = response.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resume setup');
      setIsLoading(false);
    }
  };

  // 3. Manage Account (Login Link)
  const handleLoginLink = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await connectedAccountsService.getLoginLink();
      window.open(response.url, '_blank');
      setIsLoading(false); // Reset immediately as it opens in new tab
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to access dashboard');
      setIsLoading(false);
    }
  };

  // --- UI Logic based on User State ---

  const isConnectedActive = user?.stripe_account_status === 'active';

  let buttonLabel = 'CONNECT NOW';
  let buttonAction = handleConnect;
  let statusMessage =
    'You must connect a Stripe account to receive payments from your fans. Without Stripe, you cannot earn revenue from your digital echoes.';

  if (isConnectedActive) {
    buttonLabel = 'VIEW STRIPE DASHBOARD';
    buttonAction = handleLoginLink;
    statusMessage =
      'You can now receive payments from your fans. All earnings will be transferred directly to your Stripe account.';
  } else if (!user?.stripe_account_id) {
    buttonLabel = 'CONNECT NOW';
    buttonAction = handleConnect;
  } else if (user.stripe_onboarding_completed === false) {
    buttonLabel = 'CONTINUE SETUP';
    buttonAction = handleRefreshLink;
    statusMessage = 'Your Stripe setup is incomplete. Please finish onboarding to enable payments.';
  } else if (user.stripe_account_status === 'restricted') {
    buttonLabel = 'ACTION REQUIRED';
    buttonAction = handleRefreshLink;
    statusMessage = 'Your account is restricted. Please update your information to resume payouts.';
  } else if (user.stripe_account_status === 'disabled') {
    buttonLabel = 'VIEW STRIPE DASHBOARD';
    buttonAction = handleLoginLink;
    statusMessage =
      'Your Stripe account is under review. Payments will be enabled once Stripe verifies your details.';
  } else {
    // Fallback for pending/unknown states
    buttonLabel = 'MANAGE ACCOUNT';
    buttonAction = handleLoginLink;
  }

  // Override text for Coming Soon state
  if (IS_COMING_SOON) {
    statusMessage = 'Coming Soon';
  }

  // Styles: Active state gradient border (accent-200 -> accent-350) with solid inner background
  const borderGradientStyle = isConnectedActive
    ? {
        backgroundImage: `
          linear-gradient(179.97deg, #4B3783 0.03%, #2C204C 43.73%, #110C1D 80.97%),
          linear-gradient(270deg, #ABF56E 0%, #69C71C 100%)
        `,
      }
    : undefined;

  return (
    <div
      className={clsx(
        'relative flex flex-col justify-between p-6',
        'w-full xl:w-[388px]',
        'h-auto min-h-[262px]',
        'rounded-20',
        'overflow-hidden',
        // Default (inactive): solid border + card background
        (!isConnectedActive || IS_COMING_SOON) && 'border-[6px] border-brand-300 bg-stripe-card',
        // Active: transparent border for gradient
        isConnectedActive && !IS_COMING_SOON && 'border-[6px] border-transparent',
        IS_COMING_SOON && 'cursor-not-allowed opacity-60 grayscale'
      )}
      style={
        isConnectedActive && !IS_COMING_SOON
          ? {
              border: '6px solid transparent',
              backgroundClip: 'padding-box, border-box',
              backgroundOrigin: 'padding-box, border-box',
              ...borderGradientStyle,
            }
          : undefined
      }
    >
      <div className='relative z-10 flex h-full flex-col'>
        {/* Header Row */}
        <div className='mb-2 flex items-start gap-4'>
          {isConnectedActive ? (
            // Active Header
            <div className='flex items-center gap-3'>
              <img src={stripeIcon} alt='Stripe' className='size-[50px] object-contain' />
              <div className='flex flex-col'>
                <span className='font-sans text-16 font-medium leading-19 tracking-normal text-white'>
                  Stripe Account
                </span>
                <div className='flex items-center gap-2'>
                  <div className='size-2 rounded-full bg-accent-350' />
                  <span className='font-sans text-11 font-normal leading-27 tracking-normal text-white'>
                    Connected & Active
                  </span>
                </div>
              </div>
            </div>
          ) : (
            // Default Header
            <img src={stripeLogo} alt='Stripe' className='h-[57px] w-[135px] object-contain' />
          )}
        </div>

        {/* Description Text */}
        <p className='mb-6 mt-2 font-sans text-16 font-normal leading-27 text-white'>
          {statusMessage}
        </p>

        {error && <p className='mb-2 text-sm text-red-400'>{error}</p>}

        <div className='mt-auto w-full self-center'>
          {!IS_COMING_SOON && (
            <Button
              variant='glass'
              className={clsx('h-[39px] w-full', isConnectedActive && 'font-bold uppercase')}
              onClick={buttonAction}
              disabled={isLoading}
            >
              {isLoading ? 'PROCESSING...' : buttonLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
